import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  InformationCircleIcon 
} from '@heroicons/react/24/outline';

interface ApplicationStatus {
  id: string;
  status: 'pending' | 'active' | 'rejected' | 'more_info_required';
  reviewNotes?: string;
  reviewedAt?: string;
  createdAt: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

const TeacherApplicationStatus = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const [application, setApplication] = useState<ApplicationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchApplicationStatus();
  }, []);

  const fetchApplicationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch('/api/teachers/application-status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApplication(data);
      } else {
        setError('获取申请状态失败');
      }
    } catch (err) {
      setError('网络错误，请重试');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          icon: ClockIcon,
          text: '待审核',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200'
        };
      case 'active':
        return {
          icon: CheckCircleIcon,
          text: '审核通过',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200'
        };
      case 'rejected':
        return {
          icon: XCircleIcon,
          text: '审核拒绝',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200'
        };
      case 'more_info_required':
        return {
          icon: InformationCircleIcon,
          text: '需要补充信息',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200'
        };
      default:
        return {
          icon: ExclamationTriangleIcon,
          text: '未知状态',
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200'
        };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">出错了</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={fetchApplicationStatus}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              重试
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">未找到申请记录</h2>
            <p className="text-gray-600 mb-4">您还没有提交过老师申请</p>
            <button
              onClick={() => navigate('/teacher-register')}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              立即申请
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(application.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 头部 */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-900">教师申请状态</h1>
          <button
            onClick={handleLogout}
            className="text-gray-600 hover:text-gray-800"
          >
            退出登录
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* 欢迎信息 */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              欢迎，{application.user.lastName}{application.user.firstName}
            </h2>
            <p className="text-gray-600 text-sm">
              邮箱：{application.user.email}
            </p>
          </div>

          {/* 状态卡片 */}
          <div className="p-6">
            <div className={`border-2 ${statusInfo.borderColor} ${statusInfo.bgColor} rounded-lg p-6`}>
              <div className="flex items-center mb-4">
                <StatusIcon className={`h-8 w-8 ${statusInfo.color} mr-3`} />
                <div>
                  <h3 className={`text-xl font-semibold ${statusInfo.color}`}>
                    {statusInfo.text}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    申请时间：{formatDate(application.createdAt)}
                  </p>
                </div>
              </div>

              {/* 状态说明 */}
              <div className="mb-4">
                {application.status === 'pending' && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">当前状态说明</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 您的申请已成功提交，正在等待管理员审核</li>
                      <li>• 预计审核时间：3-5个工作日</li>
                      <li>• 审核过程中，您可以正常登录查看状态</li>
                      <li>• 审核结果将通过邮件和站内通知发送</li>
                    </ul>
                  </div>
                )}

                {application.status === 'active' && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">恭喜！审核已通过</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 您的教师申请已通过审核</li>
                      <li>• 现在可以使用完整的教师功能</li>
                      <li>• 可以创建课程、管理学生、查看收益等</li>
                      <li>• 欢迎加入我们的教师团队！</li>
                    </ul>
                    {application.reviewedAt && (
                      <p className="text-sm text-gray-500 mt-2">
                        审核时间：{formatDate(application.reviewedAt)}
                      </p>
                    )}
                  </div>
                )}

                {application.status === 'more_info_required' && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">需要补充信息</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 管理员已审核您的申请，需要您补充更多信息或材料</li>
                      <li>• 请查看下方的具体要求</li>
                      <li>• 您可以点击"修改申请"按钮补充相关信息</li>
                      <li>• 补充完成后将重新提交审核</li>
                    </ul>
                    {application.reviewedAt && (
                      <p className="text-sm text-gray-500 mt-2">
                        审核时间：{formatDate(application.reviewedAt)}
                      </p>
                    )}
                  </div>
                )}

                {application.status === 'rejected' && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">审核未通过</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 很遗憾，您的申请暂时未能通过审核</li>
                      <li>• 请查看下方的审核意见</li>
                      <li>• 您可以根据意见完善材料后重新申请</li>
                      <li>• 如有疑问，请联系管理员</li>
                    </ul>
                    {application.reviewedAt && (
                      <p className="text-sm text-gray-500 mt-2">
                        审核时间：{formatDate(application.reviewedAt)}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* 审核意见 */}
              {application.reviewNotes && (
                <div className="bg-white border border-gray-200 rounded-md p-4">
                  <h4 className="font-medium text-gray-900 mb-2">审核意见</h4>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">
                    {application.reviewNotes}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              刷新状态
            </button>

            <div className="flex space-x-4">
              {(application.status === 'pending' || application.status === 'more_info_required') && (
                <button
                  onClick={() => navigate('/teacher/application')}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
                >
                  修改申请
                </button>
              )}

              {application.status === 'active' && (
                <button
                  onClick={() => navigate('/teacher/dashboard')}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 flex items-center"
                >
                  进入教师控制台
                  <ArrowRightIcon className="h-4 w-4 ml-2" />
                </button>
              )}

              {application.status === 'rejected' && (
                <button
                  onClick={() => navigate('/teacher-register')}
                  className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700"
                >
                  重新申请
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 帮助信息 */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">需要帮助？</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">联系我们</h4>
              <p className="text-gray-600">
                如有任何疑问，请发送邮件至：<br />
                <a href="mailto:support@example.com" className="text-blue-600 hover:text-blue-800">
                  support@example.com
                </a>
              </p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">审核时间</h4>
              <p className="text-gray-600">
                工作日：9:00-18:00<br />
                预计审核时间：3-5个工作日
              </p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">常见问题</h4>
              <p className="text-gray-600">
                查看我们的<br />
                <a href="#" className="text-blue-600 hover:text-blue-800">
                  常见问题解答
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherApplicationStatus; 